<script setup lang="ts">
import { getCalculatedPricePresentation } from "#shared/lib/price-presentation";
import type { ProductWithDisplayTags } from "#shared/types/product-display-tag";
import { useSiteSlider } from "~/shared/lib/use-site-slider";
import { createEcommerceProduct } from "#shared/lib/ecommerce-product";
import { useEcommerceAnalytics } from "~/features/ecommerce-analytics";
import { FeatureAddToCart } from "~/features/cart";
import { getFirstAvailableProductVariant, isProductVariantAvailable } from "~/shared/lib/product-availability";
import {
    type FavoriteProductSnapshot,
    FeatureFavoriteToggle,
    resolveFavoriteProductImage,
} from "~/features/favorites";
import {
    ProductCardOverlayTags,
    ProductPriceTags,
} from "~/features/product-display-tags";
import ProductCardOptions from "./product-card-options.vue";
import ProductCardPrice from "./product-card-price.vue";
import ProductImage from "./product-image.vue";

const {
    product,
    imageWidth = 540,
    imageSizes = "(max-width: 768px) 350px, 540px",
    firstImageLoading = "eager",
    firstImageFetchPriority = "high",
    analyticsList = "Товары",
    analyticsPosition,
    allowImageSwipe = true,
    cartVariantIds,
} = defineProps<{
    product: ProductWithDisplayTags;
    imageWidth?: number;
    imageSizes?: string;
    firstImageLoading?: "lazy" | "eager";
    firstImageFetchPriority?: "auto" | "high" | "low";
    analyticsList?: string;
    analyticsPosition?: number;
    showSku?: boolean;
    allowImageSwipe?: boolean;
    cartVariantIds?: string[];
}>();
const ecommerceAnalytics = useEcommerceAnalytics();
const siteConfig = useSiteConfig();
const cardRef = ref<HTMLElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const cardWidth = ref(0);
const hasTrackedImpression = ref(false);
const slider = useSiteSlider(containerRef, {
    drag: allowImageSwipe,
}, { lazy: true, fade: true });

let sliderInitFrame: number | null = null;
const { stop: stopSliderObserver } = useIntersectionObserver(
    containerRef,
    ([entry]) => {
        if (!entry?.isIntersecting) return;

        stopSliderObserver();
        sliderInitFrame = requestAnimationFrame(() => {
            sliderInitFrame = null;
            slider.initialize();
        });
    },
    { rootMargin: "200px" },
);
const { stop: stopAnalyticsObserver } = useIntersectionObserver(
    cardRef,
    ([entry]) => {
        if (!entry?.isIntersecting) return;

        trackProductImpression();
        stopAnalyticsObserver();
    },
);

useResizeObserver(containerRef, ([entry]) => {
    cardWidth.value = entry?.contentRect.width ?? 0;
});

onBeforeUnmount(() => {
    stopSliderObserver();
    stopAnalyticsObserver();

    if (sliderInitFrame !== null) {
        cancelAnimationFrame(sliderInitFrame);
    }
});

const imageList = computed(() => product.images?.slice(0, 6));
watch(() => imageList.value?.map((image) => image.id).join(","), () => slider.update(true));
const discountPercentage = computed(() =>
    getCalculatedPricePresentation(cardVariant.value?.calculated_price).discount,
);

function onMouseOver(event: MouseEvent) {
    const size = imageList.value?.length ?? 0;
    if (size <= 1 || cardWidth.value <= 0) return;

    slider.initialize();
    const partWidth = cardWidth.value / size;

    if (event.offsetX < 0) {
        slider.to(0, true);
        return;
    }

    const index = Math.min(size - 1, Math.trunc(event.offsetX / partWidth));
    if (slider.activeIndex.value !== index) {
        slider.to(index);
    }
}

const availableVariant = computed(() =>
    getFirstAvailableProductVariant(product.variants, variant =>
        cartVariantIds ? cartVariantIds.includes(variant.id) : isProductVariantAvailable(variant),
    ),
);
const cardVariant = computed(
    () => availableVariant.value ?? product.variants?.[0],
);
const link = computed(() => {
    const path = `/products/${product.handle}`;
    return cardVariant.value?.id
        ? `${path}?variant=${cardVariant.value.id}`
        : path;
});
const favoriteProduct = computed<FavoriteProductSnapshot>(() => {
    const price = cardVariant.value?.calculated_price?.calculated_amount;

    return {
        id: product.id,
        image: resolveFavoriteProductImage(
            product.images,
            product.thumbnail,
            cardVariant.value?.metadata?.color,
        ),
        title: product.title ?? null,
        sku: cardVariant.value?.sku ?? null,
        price: typeof price === "number" ? price : null,
        link: link.value,
    };
});
function getAnalyticsProduct(variant = cardVariant.value) {
    return createEcommerceProduct(product, {
        brand: siteConfig.name,
        list: analyticsList,
        position: analyticsPosition,
        variant,
    });
}

function trackProductImpression() {
    if (hasTrackedImpression.value) return;
    hasTrackedImpression.value = true;
    ecommerceAnalytics.track({
        type: "view_item_list",
        currency: "RUB",
        products: [getAnalyticsProduct()],
    });
}

function trackProductClick(variant = cardVariant.value) {
    ecommerceAnalytics.track({
        type: "select_item",
        currency: "RUB",
        products: [getAnalyticsProduct(variant)],
    });
}

</script>
<template>
    <article
        ref="cardRef"
        class="product-card h-full flex group flex-col gap-2 lg:gap-4 w-full"
        :data-id="product.id"
    >
        <div class="relative" @mousemove="onMouseOver">
            <NuxtLink no-prefetch class="block" :to="link" @click="trackProductClick()">
                <template v-if="imageList?.length">
                    <div
                        ref="containerRef"
                        class="card-image-slider keen-slider aspect-15/18"
                        :class="{ 'is-ready': slider.ready.value }"
                        :data-keen-slider-clickable="allowImageSwipe ? '' : undefined"
                    >
                        <div
                            v-for="(image, index) of imageList"
                            :key="image.id"
                            class="keen-slider__slide size-full"
                        >
                            <ProductImage
                                class="object-cover object-center size-full"
                                :src="image.url"
                                :width="imageWidth"
                                :alt="
                                    (image.metadata?.alt as string) ??
                                    product.title
                                "
                                :loading="
                                    index === 0
                                        ? firstImageLoading
                                        : 'lazy'
                                "
                                :fetchpriority="
                                    index === 0
                                        ? firstImageFetchPriority
                                        : 'low'
                                "
                                :sizes="imageSizes"
                            />
                        </div>
                    </div>
                    <div
                        class="product-card-pagination lg:group-hover:opacity-100 lg:opacity-0 transition-opacity z-10 absolute w-full gap-1 bottom-2 px-2 flex"
                    >
                        <button
                            v-for="(_, index) in imageList.length > 1 ? imageList : []"
                            :key="index"
                            type="button"
                            class="image-pagination-bullet"
                            :class="{ 'is-active': slider.activeIndex.value === index }"
                            :aria-label="`Изображение ${index + 1}`"
                            :aria-current="slider.activeIndex.value === index ? 'true' : undefined"
                            @click.prevent.stop="slider.to(index)"
                        />
                    </div>
                </template>
                <div v-else class="aspect-15/18">
                    <ProductImage
                        alt="Product Image not found"
                        class="object-cover object-center size-full"
                        src="/not_found.png"
                    />
                </div>
            </NuxtLink>

            <ClientOnly>
                <FeatureFavoriteToggle
                    :product="favoriteProduct"
                    class="absolute right-2 top-2 z-40 flex size-6 lg:size-10 cursor-pointer items-center justify-center rounded-full bg-white/90 text-black transition-colors hover:bg-white"
                    icon-class="!mb-0 text-sm! lg:text-xl!"
                />
                <template #fallback>
                    <span
                        class="absolute right-2 top-2 z-40 flex size-6 lg:size-10 items-center justify-center rounded-full bg-white/90 text-black"
                    >
                        <SvgoHeart
                            aria-hidden="true"
                            filled
                            class="!mb-0 text-sm! lg:text-xl!"
                        />
                    </span>
                </template>
            </ClientOnly>

            <ProductCardOverlayTags
                :tags="product.product_display_tags"
                :default-tags="product.metadata?.productType"
                :discount-percentage="discountPercentage"
            />
            <!-- <ProductCardOptions
                class="group-hover:translate-y-0 transition-all translate-y-4 opacity-0 group-hover:opacity-100"
                :product="product"
                @select="trackProductClick"
                @mousemove.stop
            /> -->
        </div>
        <div class="flex h-full flex-col gap-2">
            <span v-if="showSku" class="text-gray -mt-1 block">Арт. {{product.metadata?.model}}-{{product.metadata?.color_code}}</span>
            <div
                class="text-xs lg:text-base leading-4 lg:leading-6 uppercase font-medium"
            >
                {{ product.title }}
            </div>

            <ProductPriceTags
            class="mt-auto"
            :tags="product.product_display_tags" />
            <div class="flex items-start gap-2">
                <ProductCardPrice
                    v-if="cardVariant?.calculated_price"
                    :calculated_price="cardVariant.calculated_price"
                />
                <FeatureAddToCart
                    v-if="availableVariant"
                    :product="product"
                    :variant="availableVariant"
                    icon-only
                    class="ml-auto shrink-0"
                    @click.stop
                />
            </div>
        </div>
    </article>
</template>

<style lang="css">
.product-card-pagination .image-pagination-bullet {
    width: 100%;
    height: 2px;
    border-radius: 4px;
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.6);
}
.product-card-pagination .image-pagination-bullet.is-active {
    background-color: rgba(255, 255, 255, 1);
}

.product-card .card-image-slider.keen-slider > .keen-slider__slide {
    position: absolute;
    inset: 0;
    opacity: 0;
}
.card-image-slider > .keen-slider__slide:first-child {
    opacity: 1;
}
.card-image-slider:not(.is-ready) > div:not(:first-child) .ui-image-shell {
    display: none;
}
</style>
